import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, profileAPI } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in (check token)
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token) {
      // Try to use stored user first for faster load
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          // Invalid stored user, will fetch from API
        }
      }
      
      // Fetch user data from API to ensure it's up to date
      authAPI.getMe()
        .then(userData => {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      if (response.requires_verification) {
        return { success: true, requires_verification: true, email: response.email };
      }
      localStorage.setItem('token', response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      return { success: true };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const verifyCode = async (email, code) => {
    try {
      const response = await authAPI.verifyCode(email, code);
      localStorage.setItem('token', response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      return { success: true };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Verification failed');
    }
  };

  const resendCode = async (email) => {
    try {
      await authAPI.resendCode(email);
      return { success: true };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to resend code');
    }
  };

  const signup = async (email, password, firstName, lastName) => {
    try {
      const response = await authAPI.signup(email, password, firstName, lastName);
      localStorage.setItem('token', response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
      return { success: true };
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Signup failed');
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const updateApplicationData = async (section, data) => {
    // This will be handled by application-specific API calls
    // For now, just update local state
    const updatedUser = {
      ...user,
      applicationData: {
        ...user.applicationData,
        [section]: data
      }
    };
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyCode, resendCode, signup, logout, updateApplicationData }}>
      {children}
    </AuthContext.Provider>
  );
};